/**
 * TeamWiseCurrentQtrProjeectionCard.jsx
 *
 * Quarterly version of the Team-Wise Projection chart.
 * Shows current quarter projections (Low/Mid/High) broken down by team.
 *
 * Data Source: Time Series projection of Closed Won
 * Redirect Dashboard: "Time Series projection of Closed Won"
 *
 * This component uses the useQuarterlyTeamProjectionData hook which:
 * - Fetches data from Time Series Prediction v2 API for each team in parallel
 * - Uses local state (self-contained, not Redux)
 * - Transforms data using shared utility functions
 *
 * See also: TeamWiseCurrentMonthProjectionCard.jsx for monthly version
 */

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import D3Linearity from "./D3Linearity";
import RccCard from "../RccCard";
import { useQuarterlyTeamProjectionData } from "../hooks/useTeamProjectionData";

/**
 * Team-Wise Current Quarter Projection Card Component
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Card title (default: "Team-Wise Current Quarter Projection")
 * @param {string} props.redirectToDashboard - Dashboard to redirect on zoom
 *   (default: "Time Series projection of Closed Won")
 * @returns {JSX.Element} The quarterly team projection card
 */
function TeamWiseCurrentQtrProjeectionCard({ title, redirectToDashboard }) {
  const user = useSelector((state) => state.app?.user);

  // Use the quarterly team data hook - all fetching/transformation logic is encapsulated
  const { teamData, appliedFilters, isLoading, error } =
    useQuarterlyTeamProjectionData();

  const filteredTeamData = useMemo(() => {
    return (teamData || []).filter((row) => {
      const teamName = String(row?.team || "").trim().toLowerCase();
      return teamName !== "other";
    });
  }, [teamData]);

  const handleZoom = () => {
    console.log("Zooming Team Projection Chart...");
  };

  return (
    <RccCard
      title={title || "Team-Wise Current Quarter Projection"}
      isLoading={isLoading}
      onZoomClick={handleZoom}
      redirectToDashboard={
        redirectToDashboard || "Time Series projection of Closed Won"
      }
      appliedFilters={appliedFilters}
      focusVariant="current"
    >
      {/* Wrapper to ensure D3 Chart has relative positioning context */}
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {error ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "#999",
            }}
          >
            {error}
          </div>
        ) : filteredTeamData.length > 0 ? (
          // D3Linearity uses xField="team" to display team names on x-axis
          <D3Linearity
            data={filteredTeamData}
            xField="team"
            currencySymbol={user?.Currency || ""}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: "#999",
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default TeamWiseCurrentQtrProjeectionCard;
